"use client";
import { useCallback } from "react";

import type { IWatchlistItem } from "src/lib/contexts/ContextTypes";
import { useUserContext } from "src/lib/contexts/UserContext";
import { emptyMovieActivity } from "src/lib/filmwebid/IMovieActivity";
import { useMovieActivityOptimisticUpdater } from "src/lib/filmwebid/movieActivityMutationOptimisticUpdater";
import type { IFilmwebIdMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import { getMovieIdentifierQueryParam } from "src/lib/filmwebid/useMovieIdentifier";
import { useUserApiMutation } from "src/lib/filmwebid/useUserApiMutation";
import { useUserApiQueryKey } from "src/lib/filmwebid/useUserApiQuery";

type UseWatchListArgs = {
	movieIdentifier: IFilmwebIdMovieIdentifier | null | undefined;
	onComplete?: () => void;
	initialValue?: boolean | null;
}

export default function useWatchlist({ movieIdentifier, onComplete, initialValue }: UseWatchListArgs): [(value: boolean | null) => void, boolean] {

	const { updateWatchlistIdList } = useUserContext();

	const queryKey = useUserApiQueryKey("/api/Movie/MovieActivity", getMovieIdentifierQueryParam(movieIdentifier));

	const optimisticUpdater = useMovieActivityOptimisticUpdater(queryKey);

	const { mutate, isPending } = useUserApiMutation("/api/Watchlist", { allowAnon: false }, {
		onMutate: (variables) => optimisticUpdater(old => {
			const newWatchlistedValue = variables.method === "POST" ? true : false;
			return {
				...old,
				movieUserInteraction: {
					...(old?.movieUserInteraction ?? emptyMovieActivity.movieUserInteraction!),
					watchlisted: newWatchlistedValue
				},
				movieStats: {
					...(old?.movieStats ?? emptyMovieActivity.movieStats!),
					watchlistCount: newWatchlistedValue ? (old?.movieStats?.watchlistCount ?? 0) + 1 : (old?.movieStats?.watchlistCount ?? 0) - 1
				}
			}
		}),
		onSuccess: onComplete
	});

	const setWatchlist = useCallback((value: boolean | null) => {
		if (!movieIdentifier) return;
		const optimisticItem: IWatchlistItem = {
			movieIdentifier,
			movieIdentifiers: [movieIdentifier]
		};
		updateWatchlistIdList(optimisticItem, /* remove = */ !value);
		if (value) {
			mutate({
				method: "POST",
				body: { movieIdentifier, source: window?.location?.hostname ?? null }
			});
		} else {
			mutate({
				method: "DELETE",
				params: { [movieIdentifier.type]: movieIdentifier.value }
			});
		}
	}, [movieIdentifier, mutate, updateWatchlistIdList]);

	return [setWatchlist, isPending];
}
